import { Component, OnInit, Inject, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { SmartPaymentSolutionService } from '../../shared/services/smart-payment-link.service';
import { UserService } from '../../shared/services/getUser.service';
import { ServiceCenterService } from '../../shared/services/service-center.service';
import { AccountService } from '../../shared/services/account.service';
import { CurrencyService } from '../../shared/services/currency.service';
import { StripeTokenService } from '../../shared/services/stripe-token.service';
import { PaymentOrdersService } from '../../shared/services/payment-orders.service';
import { SidneyWorkspaceService } from '../../shared/services/sidney-workspace.service';
import { environment } from '../../../environments/environment';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-short-link-loader',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  template: `
    <div
      style="display:flex;align-items:center;justify-content:center;height:100vh;flex-direction:column"
    >
      <div style="margin-bottom:12px">Cargando link...</div>
      <mat-progress-spinner
        diameter="40"
        mode="indeterminate"
        color="black"
      ></mat-progress-spinner>
    </div>
  `,
})
export class ShortLinkLoaderComponent implements OnInit {
  private currentShortLink: string | null = null;

  constructor(
    private router: Router,
    private smart: SmartPaymentSolutionService,
    private userService: UserService,
    private serviceCenterService: ServiceCenterService,
    private accountService: AccountService,
    private currencyService: CurrencyService,
    private stripeTokenService: StripeTokenService,
    private paymentOrdersService: PaymentOrdersService,
    private sidneyWorkspaceService: SidneyWorkspaceService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  private async applyServiceCenter(resp: any): Promise<void> {
    try {
      const sc = resp?.response?.serviceCenter ?? resp;
      const rawTheme = sc?.theme ?? null;
      const rawRestrictions = sc?.restrictions ?? null;
      const safeParse = (input: any) => {
        if (!input) return {};
        if (typeof input === 'object') return input;
        const s = String(input).trim();
        try {
          return JSON.parse(s);
        } catch (e1) {
          try {
            return JSON.parse(s.replace(/'/g, '"'));
          } catch (e2) {
            try {
              return JSON.parse(s.replace(/^"+|"+$/g, ''));
            } catch (e3) {
              console.warn('[ShortLinkLoader] safeParse failed', s, e3);
              return {};
            }
          }
        }
      };
      const theme: any = safeParse(rawTheme);
      const restrictions: any = safeParse(rawRestrictions);

      // Persist restriction flags & bank numbers
      localStorage.setItem('bankNumber', '');
      const bn = restrictions?.bankNumbers;
      if (Array.isArray(bn) || (bn && typeof bn === 'object')) {
        try {
          localStorage.setItem('bankNumber', JSON.stringify(bn));
        } catch {
          localStorage.setItem('bankNumber', '');
        }
      } else if (bn !== null && typeof bn !== 'undefined') {
        localStorage.setItem('bankNumber', String(bn));
      }
      if (typeof restrictions?.useFirstDayRate !== 'undefined')
        localStorage.setItem(
          'useFirstDayRate',
          String(restrictions.useFirstDayRate),
        );
      if (typeof restrictions?.restricRegister !== 'undefined')
        localStorage.setItem(
          'restricRegister',
          String(restrictions.restricRegister),
        );
      if (typeof restrictions?.restricQuote !== 'undefined')
        localStorage.setItem('restricQuote', String(restrictions.restricQuote));
      if (typeof restrictions?.restricMessage !== 'undefined')
        localStorage.setItem(
          'restricMessage',
          String(restrictions.restricMessage),
        );
      if (typeof restrictions?.MercantilButton !== 'undefined')
        localStorage.setItem(
          'MercantilButton',
          String(restrictions.MercantilButton),
        );

      if (sc?.slug) localStorage.setItem('slug', sc.slug);
      if (sc?.id) localStorage.setItem('scid', sc.id);

      // Stripe setting id fetch (publishable key stored by StripeTokenService elsewhere)
      try {
        const settingStripeId =
          sc?.settingStripeId ||
          sc?.setting?.settingStripeId ||
          sc?.setting?.stripeId ||
          null;
        if (settingStripeId) {
          localStorage.setItem('settingStripeId', String(settingStripeId));
          // Fetch the full Stripe settings (publishable key) immediately
          this.stripeTokenService
            .getStripeSetting(String(settingStripeId))
            .subscribe({
              next: (sresp) => {
                console.log(
                  '[ShortLinkLoader] Fetching Stripe settings for settingStripeId success:',
                  sresp,
                );
              },
              error: (serr) => {
                console.warn(
                  '[ShortLinkLoader] Failed to fetch Stripe settings for settingStripeId:',
                  serr,
                );
              },
            });
        }
      } catch (e) {
        console.warn('[ShortLinkLoader] error processing stripe setting id', e);
      }

      if (theme?.primaryColor) {
        document.documentElement.style.setProperty(
          '--theme-color-primary',
          theme.primaryColor,
        );
        // Persist primary color so other screens can restore theme on refresh
        try {
          localStorage.setItem('ColorP', String(theme.primaryColor));
        } catch (e) {
          /* ignore localStorage color write failures */
        }
      }
      if (theme?.primaryTextColor) {
        document.documentElement.style.setProperty(
          '--theme-color-secondary',
          theme.primaryTextColor,
        );
        // Persist secondary color for later reuse
        try {
          localStorage.setItem('ColorS', String(theme.primaryTextColor));
        } catch (e) {
          /* ignore localStorage color write failures */
        }
      }
      if (theme?.logo) localStorage.setItem('logo', theme.logo);

      // Set the document title from the service center name (persist as appName)
      try {
        const scName =
          sc?.name ?? sc?.displayName ?? sc?.title ?? sc?.companyName ?? null;
        if (scName) {
          const nameStr = String(scName);
          try {
            document.title = nameStr;
          } catch (e) {
            /* ignore DOM errors */
          }
          try {
            localStorage.setItem('appName', nameStr);
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        console.warn(
          '[ShortLinkLoader] failed to set document title from service center',
          e,
        );
      }

      // Set document title from service center name (persist as appName)
      try {
        const scName =
          sc?.name ??
          sc?.title ??
          sc?.displayName ??
          theme?.appName ??
          sc?.serviceCenterName ??
          sc?.companyName ??
          null;
        if (scName) {
          const nameStr = String(scName);
          try {
            document.title = nameStr;
          } catch (e) {
            /* ignore DOM title set errors */
          }
          try {
            localStorage.setItem('appName', nameStr);
          } catch (e) {
            /* ignore localStorage write failures */
          }
        }
      } catch (e) {
        console.warn(
          '[ShortLinkLoader] failed to set document title from service center',
          e,
        );
      }

      // Set favicon: service center favicon (theme.fav) uses API host; fallback uses innerUrl host.
      try {
        const favRaw = theme?.fav || null;
        const scIdCurrent: string =
          sc?.id || sc?.serviceCenterId || sc?.uuid || 'unknown';
        const prevScId = localStorage.getItem('fav_sc_id');
        const prevHref = localStorage.getItem('fav');
        const innerHost = (environment.innerUrl || '').replace(/\/$/, '');
        const apiHost = (environment.api || '').replace(/\/$/, '');
        // Build fallback from innerUrl first (requirement); if absent use api host.
        const fallbackHref = innerHost
          ? innerHost + '/ServlyIcon.png'
          : apiHost
            ? apiHost + '/ServlyIcon.png'
            : this.resolveFavHref('/ServlyIcon.png');

        // When service center supplies a favicon: prefix leading '/' with apiHost (NOT innerUrl).
        let finalHref: string;
        if (favRaw) {
          const favStr = String(favRaw).trim();
          if (/^[a-zA-Z0-9]+:\/\//.test(favStr)) {
            finalHref = favStr; // absolute provided
          } else if (favStr.startsWith('/')) {
            finalHref = apiHost
              ? apiHost + favStr
              : this.resolveFavHref(favStr);
          } else {
            // relative without leading slash; leave as-is (could be CDN or already resolved by index.html script later)
            finalHref = favStr;
          }
        } else {
          finalHref = fallbackHref; // no favicon: use innerUrl fallback
        }
        // Decide if we must force update:
        // 1. Service center changed and new one has no favicon (avoid inheriting previous SC favicon)
        // 2. No previous favicon stored
        // 3. Different href computed
        const scChangedNoFav = !favRaw && prevScId && prevScId !== scIdCurrent;
        const needUpdate =
          scChangedNoFav || !prevHref || prevHref !== finalHref;
        if (needUpdate) {
          this.changeFavicon(finalHref);
          try {
            localStorage.setItem('fav', finalHref);
          } catch {}
          try {
            localStorage.setItem('fav_sc_id', scIdCurrent);
          } catch {}
          if (this.currentShortLink) {
            try {
              localStorage.setItem(
                'fav_for_shortlink_' + this.currentShortLink,
                finalHref,
              );
            } catch {}
          }
        }
      } catch (e) {
        console.warn(
          '[ShortLinkLoader] failed to set title/favicon from service center',
          e,
        );
      }

      // Fetch account details if present
      const accountId = sc?.accountId ?? sc?.account?.id ?? null;
      // Persist the raw account id early so downstream components don't see 'null'
      try {
        localStorage.setItem('owneraccount', String(accountId));
      } catch (e) {
        console.warn(
          '[ShortLinkLoader] failed to persist owneraccount early',
          e,
        );
      }

      // Fetch account-level flags (stripe, transferAndDeposit, settingsMobile)
      try {
        if (accountId) {
          const promises = [];

          // Calling the new AccountService as requested
          promises.push(
            new Promise<void>((resolve) => {
              this.accountService.getAccount(String(accountId)).subscribe({
                next: (accountResp: any) => {
                  console.log(
                    '[ShortLinkLoader] AccountService.getAccount response:',
                    accountResp,
                  );
                  try {
                    if (accountResp) {
                      const multiCurrency = !!accountResp.multiCurrency;
                      const baseCurrencyId = accountResp.baseCurrencyId;
                      localStorage.setItem(
                        'multiCurrency',
                        String(multiCurrency),
                      );
                      if (baseCurrencyId) {
                        localStorage.setItem(
                          'baseCurrencyId',
                          String(baseCurrencyId),
                        );
                      }

                      // Apply conditional currency persistence
                      if (multiCurrency) {
                        const vesId =
                          environment.vesId ||
                          'cfb82af5-8249-4a23-b3fd-b323af04bfe5';
                        localStorage.setItem('accountcurrency', vesId);
                        localStorage.setItem('accountcurrency_iso', 'VES');
                      } else if (baseCurrencyId) {
                        localStorage.setItem(
                          'accountcurrency',
                          String(baseCurrencyId),
                        );
                        const vesId =
                          environment.vesId ||
                          'cfb82af5-8249-4a23-b3fd-b323af04bfe5';
                        if (String(baseCurrencyId) === vesId) {
                          localStorage.setItem('accountcurrency_iso', 'VES');
                        } else {
                          localStorage.setItem('accountcurrency_iso', 'USD');
                        }
                      }
                    } else {
                      // Apply safe fallback when accountResp is null
                      const vesId = environment.vesId || 'cfb82af5-8249-4a23-b3fd-b323af04bfe5';
                      localStorage.setItem('multiCurrency', 'true');
                      localStorage.setItem('accountcurrency', vesId);
                      localStorage.setItem('accountcurrency_iso', 'VES');
                    }
                  } catch (e) {
                    console.warn(
                      '[ShortLinkLoader] error persisting multiCurrency/baseCurrencyId',
                      e,
                    );
                  }
                  resolve();
                },
                error: (err) => {
                  console.warn(
                    '[ShortLinkLoader] AccountService.getAccount failed -> applying safe fallback (multiCurrency = true, currency = VES):',
                    err,
                  );
                  try {
                    const vesId = environment.vesId || 'cfb82af5-8249-4a23-b3fd-b323af04bfe5';
                    localStorage.setItem('multiCurrency', 'true');
                    localStorage.setItem('accountcurrency', vesId);
                    localStorage.setItem('accountcurrency_iso', 'VES');
                  } catch (e) {
                    console.warn('[ShortLinkLoader] fallback write failed', e);
                  }
                  resolve();
                },
              });
            }),
          );

          promises.push(
            new Promise<void>((resolve) => {
              this.userService.getAccountById(String(accountId)).subscribe({
                next: (acc: any) => {
                  try {
                    if (acc) {
                      localStorage.setItem('stripe', String(acc.stripe !== false));
                      localStorage.setItem('transferAndDeposit', String(acc.transferAnddeposit !== false));
                      localStorage.setItem('settingsMobile', String(acc.settingsMobile !== false));
                    } else {
                      localStorage.setItem('stripe', 'true');
                      localStorage.setItem('transferAndDeposit', 'true');
                      localStorage.setItem('settingsMobile', 'true');
                    }
                  } catch (e) {
                    console.warn(
                      '[ShortLinkLoader] failed to persist account flags',
                      e,
                    );
                  }
                  resolve();
                },
                error: (err: any) => {
                  console.warn(
                    '[ShortLinkLoader] getAccountById failed -> applying safe fallback (stripe = true, transfer = true, mobile = true):',
                    err,
                  );
                  try {
                    localStorage.setItem('stripe', 'true');
                    localStorage.setItem('transferAndDeposit', 'true');
                    localStorage.setItem('settingsMobile', 'true');
                    localStorage.setItem('bankNumber', '3'); // default mobile bank type
                  } catch (e) {
                    console.warn(
                      '[ShortLinkLoader] failed to persist fallback flags',
                      e,
                    );
                  }
                  resolve();
                },
              });
            }),
          );

          await Promise.all(promises);
        }
      } catch (e) {
        console.warn('[ShortLinkLoader] error calling getAccountById', e);
      }
    } catch (e) {
      console.warn('[ShortLinkLoader] applyServiceCenter failed', e);
    }
  }

  private changeFavicon(iconPath: string): void {
    try {
      const head =
        this.document.head || this.document.getElementsByTagName('head')[0];
      // Check if the first existing favicon already matches the desired path — if so, avoid touching DOM
      const existingIcons = head.querySelectorAll(
        "link[rel~='icon'], link[rel='shortcut icon']",
      );
      if (existingIcons && existingIcons.length > 0) {
        try {
          const firstHref =
            (existingIcons[0] as HTMLLinkElement).getAttribute('href') || '';
          if (firstHref === iconPath) return; // already set, no-op to avoid reload/flicker
        } catch (e) {
          // continue to replace if any check fails
        }
      }
      // Remove any existing favicon links
      existingIcons.forEach((el) => {
        try {
          this.renderer.removeChild(head, el);
        } catch (e) {
          // ignore
        }
      });

      const link: HTMLLinkElement = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'type', 'image/x-icon');
      this.renderer.setAttribute(link, 'rel', 'icon');
      this.renderer.setAttribute(link, 'href', iconPath);
      this.renderer.appendChild(head, link);
    } catch (e) {
      console.warn('[ShortLinkLoader] changeFavicon failed', e);
    }
  }

  private getFileUploadHost(): string {
    // Host used for generic resolution (kept for legacy resolveFavHref of relative paths not covered above)
    // We now prefer api for service center favs; innerUrl only for fallback icon path construction earlier.
    try {
      if (environment?.api) return String(environment.api).replace(/\/$/, '');
    } catch (e) {
      /* ignore */
    }
    try {
      if (environment?.innerUrl)
        return String(environment.innerUrl).replace(/\/$/, '');
    } catch (e) {
      /* ignore */
    }
    try {
      return window.location.origin.replace(/\/$/, '');
    } catch (e) {
      return '';
    }
  }

  private resolveFavHref(favStr: string): string {
    try {
      const s = String(favStr || '');
      if (!s) return s;
      if (/^[a-zA-Z0-9]+:\/\//.test(s)) return s; // already absolute
      if (s.startsWith('/')) {
        const host = this.getFileUploadHost();
        if (host) return host + s;
      }
      return s; // leave relative paths as-is
    } catch (e) {
      return String(favStr || '');
    }
  }

  private findValueByKey(obj: any, targetKey: string): any {
    if (!obj || typeof obj !== 'object') return null;
    const targetLower = targetKey.toLowerCase();
    
    // 1. Direct match at this level (case-insensitive)
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase() === targetLower) {
        const val = obj[k];
        if (val !== null && val !== undefined && typeof val !== 'object') {
          return val;
        }
      }
    }
    
    // 2. Traversal of nested objects
    for (const k of Object.keys(obj)) {
      const val = obj[k];
      if (val && typeof val === 'object') {
        const result = this.findValueByKey(val, targetKey);
        if (result !== null && result !== undefined) {
          return result;
        }
      }
    }
    return null;
  }

  private bootstrapPaymentSession(
    orderCorrelative: string | null,
    serviceCenterId: string | null,
    customerId: string | null,
    emails: string | null,
    workSpaceId?: string | null
  ): void {
    if (orderCorrelative) {
      localStorage.setItem('orderCorrelative', String(orderCorrelative));
    }
    if (serviceCenterId) {
      localStorage.setItem('scid', String(serviceCenterId));
    }
    if (customerId) {
      localStorage.setItem('cid', String(customerId));
    }
    if (emails) {
      localStorage.setItem('email', String(emails));
    }

    let hasNavigated = false;
    const navigateToPayment = () => {
      if (!hasNavigated) {
        hasNavigated = true;
        this.router.navigateByUrl('/payment', { replaceUrl: true });
      }
    };

    // Safety fallback timer: guarantee navigation after 3 seconds
    setTimeout(() => {
      if (!hasNavigated) {
        console.warn('[ShortLinkLoader] Safety timeout triggered: forcing navigation to /payment');
        navigateToPayment();
      }
    }, 3000);

    const validScid = serviceCenterId && serviceCenterId !== 'undefined' && serviceCenterId !== 'null' ? serviceCenterId : null;

    if (validScid) {
      this.fetchAndApplyServiceCenter(validScid, navigateToPayment);
    } else {
      navigateToPayment();
    }
  }

  private fetchAndApplyServiceCenter(serviceCenterId: string, callback: () => void): void {
    this.serviceCenterService.showServiceCenterId(serviceCenterId).subscribe({
      next: async (scResp: any) => {
        await this.applyServiceCenter(scResp);
        const sc = scResp?.response?.serviceCenter ?? scResp;
        const settingStripeId = sc?.settingStripeId || sc?.setting?.settingStripeId || sc?.setting?.stripeId || null;
        if (settingStripeId) {
          this.stripeTokenService.getStripeSetting(String(settingStripeId)).subscribe({
            next: () => callback(),
            error: () => callback()
          });
        } else {
          callback();
        }
      },
      error: () => {
        callback();
      }
    });
  }

  ngOnInit(): void {
    // Clear any previous persisted state before loading a new short link context
    try {
      // Preserve favicon and appName to avoid flicker: read them, clear storage, then restore
      let preservedFav: string | null = null;
      let preservedAppName: string | null = null;
      try {
        preservedFav = localStorage.getItem('fav');
      } catch (e) {
        preservedFav = null;
      }
      try {
        preservedAppName = localStorage.getItem('appName');
      } catch (e) {
        preservedAppName = null;
      }
      localStorage.clear();
      if (preservedFav !== null) {
        try {
          localStorage.setItem('fav', preservedFav);
        } catch (e) {
          /* ignore */
        }
      }
      if (preservedAppName !== null) {
        try {
          localStorage.setItem('appName', preservedAppName);
        } catch (e) {
          /* ignore */
        }
      }
      console.log(
        '[ShortLinkLoader] Cleared previous localStorage state (preserved fav/appName)',
      );
    } catch (e) {
      console.warn('[ShortLinkLoader] Failed to clear localStorage', e);
    }

    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '';
    const shortLink = pathname.replace(/^\//, '') || '';
    this.currentShortLink = shortLink || null;
    console.log('[ShortLinkLoader] extracted shortLink from URL:', shortLink);
    if (!shortLink) {
      this.router.navigateByUrl('/payment', { replaceUrl: true });
      return;
    }
    // Persist it: PaymentComponent needs the short link to resolve the workspace.
    try {
      localStorage.setItem('shortLink', shortLink);
    } catch (e) {
      console.warn('[ShortLinkLoader] failed to persist shortLink', e);
    }
    // Drop any workspace identity cached for a previously loaded link.
    this.sidneyWorkspaceService.reset();

    // Fetch smart payment solution data from /api/v1/smart-payment-solutions/{shortLink}
    this.smart.getSmartPaymentSolution(shortLink).subscribe({
      next: (sol: any) => {
        console.log('[ShortLinkLoader] getSmartPaymentSolution config fetched:', sol);
        try {
          if (sol) {
            localStorage.setItem('smart_payment_solution', JSON.stringify(sol));

            // Extract and persist specified fields using robust recursive key search
            const userId = this.findValueByKey(sol, 'userId');
            const workSpaceId = this.findValueByKey(sol, 'workSpaceId');
            const orderId = this.findValueByKey(sol, 'orderId') || this.findValueByKey(sol, 'paymentOrderId');

            console.log('[ShortLinkLoader] Extracted fields:', { userId, workSpaceId, orderId });

            if (userId) localStorage.setItem('userId', String(userId));
            if (workSpaceId) localStorage.setItem('workSpaceId', String(workSpaceId));
            
            if (orderId) {
              localStorage.setItem('orderId', String(orderId));
              // Fetch order details by ID to extract name, precio, and bootstrap payment details
              this.paymentOrdersService.getOrderById(String(orderId)).subscribe({
                next: (order: any) => {
                  console.log('[ShortLinkLoader] getOrderById response:', order);
                  try {
                    if (order) {
                      localStorage.setItem('payment_order', JSON.stringify(order));
                      // Extract name and price details robustly using recursive key search
                      const name = this.findValueByKey(order, 'name') || '';
                      const precio = this.findValueByKey(order, 'precio') || this.findValueByKey(order, 'price') || this.findValueByKey(order, 'amount') || this.findValueByKey(order, 'total') || '';
                      
                      if (name) localStorage.setItem('name', String(name));
                      if (precio) localStorage.setItem('precio', String(precio));

                      // Extract order level properties for bootstrapping
                      const orderCorrelative = this.findValueByKey(order, 'orderCorrelative') || this.findValueByKey(order, 'correlative');
                      const serviceCenterId = this.findValueByKey(order, 'serviceCenterId') || this.findValueByKey(order, 'scid');
                      const customerId = this.findValueByKey(order, 'customerId') || this.findValueByKey(order, 'cid');
                      const emails = this.findValueByKey(order, 'emails') || this.findValueByKey(order, 'email');

                      console.log('[ShortLinkLoader] Bootstrapping session with:', { orderCorrelative, serviceCenterId, customerId, emails });
                      this.bootstrapPaymentSession(orderCorrelative, serviceCenterId, customerId, emails, workSpaceId);
                    } else {
                      this.router.navigateByUrl('/payment', { replaceUrl: true });
                    }
                  } catch (e) {
                    console.warn('[ShortLinkLoader] failed to extract data from order', e);
                    this.router.navigateByUrl('/payment', { replaceUrl: true });
                  }
                },
                error: (err: any) => {
                  console.warn('[ShortLinkLoader] getOrderById failed:', err);
                  this.router.navigateByUrl('/payment', { replaceUrl: true });
                }
              });
            } else {
              console.warn('[ShortLinkLoader] orderId not found in smart payment solution config.');
              this.router.navigateByUrl('/payment', { replaceUrl: true });
            }
          } else {
            this.router.navigateByUrl('/payment', { replaceUrl: true });
          }
        } catch (e) {
          console.warn('[ShortLinkLoader] failed to store smart_payment_solution data', e);
          this.router.navigateByUrl('/payment', { replaceUrl: true });
        }
      },
      error: (err: any) => {
        console.warn('[ShortLinkLoader] getSmartPaymentSolution failed:', err);
        this.router.navigateByUrl('/payment', { replaceUrl: true });
      }
    });
  }
}
